<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'w-full sm:max-w-md' }">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard :ui="{ rounded: 'rounded-lg', body: { base: 'overflow-visible' } }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Edit Registered Contact</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="phone" label="Phone">
                        <InputPhone v-model="state.phone" />
                    </UFormGroup>
                    <UFormGroup name="email" label="Email">
                        <UInput v-model="state.email" type="email" size="lg" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                        <UButton :loading="loading" type="submit" size="lg" color="black"> Save </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </UModal>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { normalizeStoredPhone, phoneDigits, phoneSchema } from '~/utils/phone'
import type { FormSubmitEvent } from '#ui/types'
import { BloodType, Gender, type Customer } from '~/types/customer'
import { useDebounce } from '@vueuse/core';

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    customer?: Customer
}>()

const client = useSupabaseClient()
const toast = useToast()

const form = ref()
const state = ref<z.infer<typeof schema>>()
const loading = ref<boolean>(false)

const schema = z.object({
    phone: phoneSchema(),
    email: z.string().email().nullable().optional(),
})

watch(() => props.open, (open) => {
    if (open) {
        state.value = {
            phone: normalizeStoredPhone(props.customer?.phone),
            email: props.customer?.email,
        }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    if (props.customer?.id) {
        loading.value = true

        try {
            await $fetch(`/api/customers/${props.customer.id}/registered-contact`, {
                method: 'PUT',
                body: {
                    phone: phoneDigits(event.data.phone) || undefined,
                    email: event.data.email,
                }
            })

            toast.add({
                title: 'Registered contact updated!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            dismiss(true)
        } catch (e: any) {
            if (e.statusCode === 400) {
                toast.add({
                    title: e.data.message,
                    color: 'orange',
                    icon: 'i-ph-x-circle-duotone'
                })
            }
            else {
                toast.add({
                    title: 'An error has occurred, try again later.',
                    color: 'red',
                    icon: 'i-ph-warning-duotone'
                })
            }

            dismiss()
        } finally {
            loading.value = false
        }
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload')
    }
}
</script>
